/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import { Card, CardMedia } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridDropdown from "../../widgets/GridDropdown";
import GridSection from "../../widgets/GridSection";

import PageNavigator, { onNext } from "../../PageNavigator";

//DATA SLICE - REDUCER
import { updateRisk } from "../../../states/slcInvestmentRisk.js";

var riskObjectiveError = "";
var riskTimeObjectiveError = "";
var riskReturnsObjectiveError = "";
var riskToleranceObjectiveError = "";
var riskResponseObjectiveError = "";
var riskRangeObjectiveError = "";

//TRUE - WHEN COMPONENT IS VALID
var isValid = false;

const marks = [
  {
    value: 1,
    label: "Lowest risk and Lowest Potential Return",
  },
  {
    value: 2,
  },
  {
    value: 3,
  },
  {
    value: 4,
  },
  {
    value: 5,
    label: "Highest risk and Highest Potential Return",
  },
];

const riskTimeObjectiveData = [
  { key: "NOT_APPLICABLE", name: "None", value: "0" },
  { key: "THREE_OR_LESS", name: "3 years or less", value: "1" },
  { key: "THREE_TO_FIVE", name: "> 3 - 5 years", value: "2" },
  { key: "FIVE_TO_SEVEN", name: "> 5 - 7 years", value: "3" },
  { key: "SEVEN_TO_TEN", name: "> 7 - 10 years", value: "4" },
  { key: "ABOVE_TEN", name: "More than 10 years", value: "5" },
];

const riskReturnsObjectiveData = [
  { key: "RETURN_NOT_APPLICABLE", name: "None", value: "0" },
  { key: "RETURN_TWO_OR_LESS", name: "0 - 2%", value: "1" },
  { key: "RETURN_TWO_TO_FOUR", name: "> 2 - 4%", value: "2" },
  { key: "RETURN_FOUR_TO_SIX", name: "> 4 - 6%", value: "3" },
  { key: "RETURN_SIX_TO_EIGHT", name: "> 6 - 8%", value: "4" },
  { key: "RETURN_ABOVE_EIGHT", name: "More than 8%", value: "5" },
];

const riskToleranceObjectiveData = [
  { key: "TOLERANCE_NOT_APPLICABLE", name: "None", value: "0" },
  { key: "TOLERANCE_TEN", name: "-10%", value: "1" },
  { key: "TOLERANCE_TWENTY", name: "-20%", value: "2" },
  { key: "TOLERANCE_THIRTY", name: "-30%", value: "3" },
  { key: "TOLERANCE_FORTY_FIVE", name: "-45%", value: "4" },
  { key: "TOLERANCE_SIXTY", name: "-60%", value: "5" },
];

const riskResponseObjectiveData = [
  { key: "RESPONSE_NOT_APPLICABLE", name: "None", value: "0" },
  {
    key: "RESPONSE_SELL_ALL",
    name: "Sell off all investments",
    value: "1",
  },
  {
    key: "RESPONSE_SELL_NO_LOSES",
    name: "Sell off those with little or no losses.",
    value: "2",
  },
  {
    key: "RESPONSE_HOLD",
    name: "Keep my investment in hope of a recovery.",
    value: "3",
  },
  { key: "RESPONSE_BUY_CAUTIOUS", name: "Buy cautiously", value: "4" },
  { key: "RESPONSE_BUY_AGGRESIVE", name: "Buy aggresively", value: "5" },
];

const riskRangeObjectiveData = [
  { key: "RANGE_NOT_APPLICABLE", name: "None", value: "0" },
  { key: "RANGE_A", name: "A", value: "1" },
  { key: "RANGE_B", name: "B", value: "2" },
  { key: "RANGE_C", name: "C", value: "3" },
  { key: "RANGE_D", name: "D", value: "4" },
  { key: "RANGE_E", name: "E", value: "5" },
];

const riskMap = new Map([
  [
    "CONSERVATIVE",
    "Your investment risk profile suggests that you are able to take on low risk investments. Your main priority is safeguarding your investment capital. You are prepared to sacrifice higher returns for peace of mind. This indicates that you wish to take a conservative approach towards investing and the recommended asset allocation strategy would range between 80% to 90% products with risk = 1 (Developed Govt Fixed Income) and 10% to 20% products with risk = 5 (Regional or Single Country Equities).",
  ],
  [
    "MODERATE",
    "Your investment risk profile suggests that you have some understanding of investment markets and their behaviour. You do not wish to see all your capital eroded away by taxes and inflation and you are prepared to take short- to medium-term risk in order to gain longer-term capital growth. This indicates that you wish to have a moderate approach towards investing and the recommended asset allocation strategy would range between 60% to 70% products with risk = 1 (Developed Govt Fixed Income) and 30% to 40% products with risk = 5 (Regional or Single Country Equities).",
  ],
  [
    "BALANCED",
    "Your investment risk profile suggests that you are seeking a greater growth component in your investment portfolio to help protect your capital from the eroding effect of taxes and inflation. While you remain cautious towards taking high levels of risk, your general understanding of investment markets enable you to feel comfortable with some short- to medium-term risk. Your priority is consistent capital growth with some income to smoothen out any volatility in your returns. This indicates that you wish to have a balanced approach towards investing and the recommended asset allocation strategy would range between 40% to 50% products with risk = 1 (Developed Govt Fixed Income) and 50% to 60% products with risk = 5 (Regional or Single Country Equities).",
  ],
  [
    "GROWTH",
    "Your investment risk profile suggests that you are an investor who understands the movement of the investment markets. You are most interested in maximising the value of your investments(s) through long-term capital growth, although you do not wish to make imbalanced investment decisions. You are comfortable with short- to medium-term volatility from your portfolio to maximise the potential for long-term capital growth. This indicates that you wish to have an assertive approach towards investing and the recommended asset allocation strategy would range between 20% to 30% products with risk = 1 (Developed Govt Fixed Income) and 70% to 80% products with risk = 5 (Regional or Single Country Equities).",
  ],
  [
    "AGGRESIVE",
    "Your investment risk profile suggests that you are prepared to sacrifice your investment capital in pursuit of highest long-term potential capital growth. You have a good understanding of the behaviour of investment markets and you are interested in negating the effects of taxation and inflation. This indicates that you wish to have an aggressive approach towards investing and the recommended asset allocation strategy would range between 0% to 10% products with risk = 1 (Developed Govt Fixed Income) and 90% to 100% products with risk = 5 (Regional or Single Country Equities).",
  ],
]);

export default function InvestmentRisk() {
  const investmentRisk = useSelector((state) => state.investmentRisk);

  const [riskObjective, setRiskObjective] = useState(investmentRisk.riskObjective);
  const [riskTimeObjective, setTimeObjective] = useState(investmentRisk.riskTimeObjective);
  const [riskReturnsObjective, setReturnsObjective] = useState(investmentRisk.riskReturnsObjective);
  const [riskToleranceObjective, setToleranceObjective] = useState(investmentRisk.riskToleranceObjective);
  const [riskResponseObjective, setResponseObjective] = useState(investmentRisk.riskResponseObjective);
  const [riskRangeObjective, setRangeObjective] = useState(investmentRisk.riskRangeObjective);
  const [riskScore, setRiskScore] = useState(investmentRisk.riskScore);
  const [riskType, setRiskType] = useState(investmentRisk.riskType);
  const [riskDesc, setRiskDesc] = useState(investmentRisk.riskDesc);

  /**
   * TRUE - WHEN COMPONENT IS VALID AND "NEXT PAGE" BUTTON IS ENABLED
   *        ALL SUB COMPONENTS SHOULD BE VALID FOR THIS TO BE TRUE
   */
  const [enableNext, setEnableNext] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    let total = riskObjective ? parseInt(riskObjective) : 0;
    total += riskTimeObjective ? parseInt(riskTimeObjective) : 0;
    total += riskReturnsObjective ? parseInt(riskReturnsObjective) : 0;
    total += riskToleranceObjective ? parseInt(riskToleranceObjective) : 0;
    total += riskResponseObjective ? parseInt(riskResponseObjective) : 0;
    total += riskRangeObjective ? parseInt(riskRangeObjective) : 0;
    setRiskScore(total);

    let riskKey = "";
    if (total >= 6 && total <= 9) riskKey = "CONSERVATIVE";
    else if (total >= 10 && total <= 14) riskKey = "MODERATE";
    else if (total >= 15 && total <= 21) riskKey = "BALANCED";
    else if (total >= 22 && total <= 26) riskKey = "GROWTH";
    else if (total >= 27 && total <= 30) riskKey = "AGGRESIVE";
    setRiskType(riskKey);

    let description = riskMap.get(riskKey);
    setRiskDesc(description);

    const data = {
      riskObjective: riskObjective,
      riskTimeObjective: riskTimeObjective,
      riskReturnsObjective: riskReturnsObjective,
      riskToleranceObjective: riskToleranceObjective,
      riskResponseObjective: riskResponseObjective,
      riskRangeObjective: riskRangeObjective,
      riskScore: total,
      riskType: riskKey,
      riskDesc: description,
    };
    updateRedux("", data, updateRisk);
  }, [
    riskObjective,
    riskTimeObjective,
    riskReturnsObjective,
    riskToleranceObjective,
    riskResponseObjective,
    riskRangeObjective,
  ]);

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    if (e instanceof PointerEvent) {
      let id = object.props.id.toLowerCase();

      if (id === "risktimeobjective") {
        riskTimeObjectiveError = "Time objective required";
        if (Number(e.target.value) !== 0) riskTimeObjectiveError = "";
        setTimeObjective(e.target.value);
      }
      if (id === "riskreturnsobjective") {
        riskReturnsObjectiveError = "Gross return required";
        if (Number(e.target.value) !== 0) riskReturnsObjectiveError = "";
        setReturnsObjective(e.target.value);
      }
      if (id === "risktoleranceobjective") {
        riskToleranceObjectiveError = "Tolerance required";
        if (Number(e.target.value) !== 0) riskToleranceObjectiveError = "";
        setToleranceObjective(e.target.value);
      }
      if (id === "riskresponseobjective") {
        riskResponseObjectiveError = "Response required";
        if (Number(e.target.value) !== 0) riskResponseObjectiveError = "";
        setResponseObjective(e.target.value);
      }
      if (id === "riskrangeobjective") {
        riskRangeObjectiveError = "Investment range required";
        if (Number(e.target.value) !== 0) riskRangeObjectiveError = "";
        setRangeObjective(e.target.value);
      }
    } else {
      setRiskObjective(e.target.value);
    }
    calculateValidity();
  }

  function calculateValidity() {
    /*
      Enable "Next" button when there's no error message
      Otherwise do early exit.
    */
    if (
      riskObjectiveError.length !== 0 ||
      riskTimeObjectiveError.length !== 0 ||
      riskReturnsObjectiveError.length !== 0 ||
      riskToleranceObjectiveError.length !== 0 ||
      riskResponseObjectiveError.length !== 0 ||
      riskRangeObjectiveError.length !== 0
    ) {
      setEnableNext(false);
      return;
    }

    setEnableNext(true);
  }

  function validateForm(e) {
    e.preventDefault();

    if (riskTimeObjective === 0) riskTimeObjectiveError = "Time objective required";

    if (riskReturnsObjective === 0) riskReturnsObjectiveError = "Gross return required";

    if (riskToleranceObjective === 0) riskToleranceObjectiveError = "Tolerance required";

    if (riskResponseObjective === 0) riskResponseObjectiveError = "Response required";

    if (riskRangeObjective === 0) riskRangeObjectiveError = "Investment range required";

    if (
      riskTimeObjective !== 0 &&
      riskReturnsObjective !== 0 &&
      riskToleranceObjective !== 0 &&
      riskResponseObjective !== 0 &&
      riskRangeObjective !== 0
    ) {
      isValid = true;
      onNext();
    }
  }

  return (
    <>
      <Box component="form" onSubmit={validateForm} noValidate>
        <Grid container spacing={2}>
          <GridSectionHeader
            sectionName="INVESTMENT RISK"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              backgroundColor: "#10B981",
            }}
          />

          {/* 1. OBJECTIVE - RISK*/}
          <GridSection columnSize="12">
            <Typography variant="body1" sx={{ pb: 2 }}>
              1. Choose your preferred risk/return objective.
            </Typography>
          </GridSection>

          <Grid size={7}>
            <Slider
              id="riskObjective"
              sx={{
                ml: 20,
              }}
              aria-label="Custom marks"
              // getAriaValueText={valuetext}
              defaultValue={riskObjective}
              step={1}
              valueLabelDisplay="auto"
              min={1}
              max={5}
              marks={marks}
              onChange={handleChange}
            />
          </Grid>

          {/* 2. OBJECTIVE - TIME*/}
          <GridSection columnSize="12">
            <Typography variant="body1" sx={{ pb: 2 }}>
              2. Time Horizon: How much time have you set aside to achieve your investment/financial objective?
            </Typography>
            <GridDropdown
              id="riskTimeObjective"
              key="riskTimeObjective"
              columnSize="8"
              // label="What is the time allocated to achieve financial/investment objectives?"
              label="Time allocated"
              defaultValue=""
              value=""
              dataType="keyNameValue"
              currState={riskTimeObjective}
              onChange={handleChange}
              data={riskTimeObjectiveData}
              error={riskTimeObjectiveError}
            />
          </GridSection>

          {/* 3. OBJECTIVE - ANNUALISED RETURN*/}
          <GridSection columnSize="12">
            <Typography variant="body1" sx={{ pb: 2 }}>
              3. What is the average annualised gross return you reasonably expect to achieve from your investment
              portfolio(s) over a longer term period of at least 10 years?
            </Typography>

            <GridDropdown
              id="riskReturnsObjective"
              key="riskReturnsObjective"
              columnSize="8"
              // label="What average annualised gross return is expected from investment portfolio?"
              label="Returns expected"
              defaultValue=""
              value=""
              dataType="keyNameValue"
              currState={riskReturnsObjective}
              onChange={handleChange}
              data={riskReturnsObjectiveData}
              error={riskReturnsObjectiveError}
            />
          </GridSection>

          {/* 4. OBJECTIVE - SEVERE CRISIS CONSIDERATION*/}
          <GridSection columnSize="12">
            <Typography variant="body1" sx={{ pb: 2 }}>
              4. In your opinion, what percentage drop is the major market indices (Eg. STI, Dow Jones, ASDAQ, S&P, etc)
              would you consider a severe crisis?
            </Typography>

            <GridDropdown
              id="riskToleranceObjective"
              key="riskToleranceObjective"
              columnSize="8"
              // label="What percentage drop in market indices would you consider a severe crisis?"
              label="Percentage drop considered as crisis"
              defaultValue=""
              value=""
              dataType="keyNameValue"
              currState={riskToleranceObjective}
              onChange={handleChange}
              data={riskToleranceObjectiveData}
              error={riskToleranceObjectiveError}
            />
          </GridSection>

          {/* 5. OBJECTIVE - RESPONSE WHEN DROP 20% */}
          <GridSection columnSize="12">
            <Typography variant="body1" sx={{ pb: 2 }}>
              5. If stock market have droipped byu at least 20% in its value over a year, how will you potentially
              respond?
            </Typography>

            <GridDropdown
              id="riskResponseObjective"
              key="riskResponseObjective"
              columnSize="8"
              // label="What would be your likely respond if stocks have dropped by at least 20%?"
              label="Possible response when market under performs"
              defaultValue=""
              value=""
              dataType="keyNameValue"
              currState={riskResponseObjective}
              onChange={handleChange}
              data={riskResponseObjectiveData}
              error={riskResponseObjectiveError}
            />
          </GridSection>

          {/* 5. OBJECTIVE - INVESTMENT RANGE */}
          <GridSection columnSize="12" spacing={2}>
            <Typography variant="body1" sx={{ pb: 2 }}>
              6. The following chart shows the possible range of values for the 5 different investments of $100,000
              after one year. Which investment would you be most comfortable in owning?
            </Typography>

            <Grid size={8} sx={{ pb: 2 }}>
              <Card variant="outlined">
                <CardMedia
                  component="img"
                  height="300"
                  src={process.env.PUBLIC_URL + `/images/investment_risk_profile_graph.jpg`}
                  alt="Investment Risk Profile"
                />
              </Card>
            </Grid>

            <GridDropdown
              id="riskRangeObjective"
              key="riskRangeObjective"
              columnSize="8"
              // label="Which model of investment would you be most comfortable owning?"
              label="Preferred investment model"
              defaultValue=""
              value=""
              dataType="keyNameValue"
              currState={riskRangeObjective}
              onChange={handleChange}
              data={riskRangeObjectiveData}
              error={riskRangeObjectiveError}
            />
          </GridSection>

          {riskType && (
            <>
              <Typography
                variant="body2"
                align="justify"
                gutterBottom
                sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "blue" }}>
                  Risk Profile Assessment
                </Typography>
                <br />
                Your Risk Profile is <b>{riskType}</b> <br />
                <br />
                {riskDesc}
              </Typography>
            </>
          )}

          <PageNavigator validator={validateForm} isValid={enableNext} />
        </Grid>
      </Box>
    </>
  );
}
